import { ArrowDropDown, ArrowRight } from "@mui/icons-material";
import { Autocomplete, createFilterOptions, FormControl, FormLabel, Typography, RadioGroup, FormControlLabel, Radio, TextField, Divider } from "@mui/material";
import { useLogin } from "providers/Login";
import { useState, useEffect } from "react";
import { collapsibleAutocompleteGroupKey, groupedSectionSlotProps, optionValuesEqual, renderOptionWithHighlight, searchTextField, useCollapsibleAutocompleteGroups } from "utils/searchableOptions";
import { HeaderConditionType } from "./BaseFilter";

export interface GroupFilterOptionsProps {
    id: string
    state?: GroupFilter
    setState: (v?: GroupFilter) => void
}

export interface GroupFilter {
    filterType: "group"
    conditionType: HeaderConditionType
    filter: string
    // groupId is an optional deconflict condition on equal/not_equal filters: it
    // narrows an exact name match to one specific catalog group, so two groups
    // sharing a name across different directories aren't both treated as the
    // same exact match. It is never required — Filter alone still matches.
    groupId?: string
    // groupName/directoryName/groupHidden are resolved by the server from
    // groupId and are read-only display info; they are not read back on save.
    groupName?: string
    directoryName?: string
    groupHidden?: boolean
}

interface GroupOption {
    id: string
    identifier: string
    provider: string
    directoryId: string
    directoryName: string
}

interface GroupSelectOption {
    value: string
    label: string
    group: GroupOption
    source: "directory" | "custom" | "hidden" | "unknown"
}

const filterGroupOptions = createFilterOptions<GroupSelectOption>();
const customGroupLabel = "Custom (typed) group name";
const customGroupKey = collapsibleAutocompleteGroupKey("custom-group", customGroupLabel);
const hiddenGroupLabel = "Hidden group";
const hiddenGroupKey = collapsibleAutocompleteGroupKey("hidden-group", hiddenGroupLabel);
const unknownGroupLabel = "Unknown group";
const unknownGroupKey = collapsibleAutocompleteGroupKey("unknown-group", unknownGroupLabel);

function customGroupOption(identifier: string): GroupSelectOption {
    return {
        value: `custom:${identifier}`,
        label: identifier,
        group: {
            id: "",
            identifier,
            provider: "",
            directoryId: "",
            directoryName: "",
        },
        source: "custom",
    };
}

// A hidden group is still a real, resolvable group - just excluded from the
// picker's own (visible-only) fetch. This placeholder lets the Autocomplete
// display and keep it selected without pretending it's a fresh pick.
function hiddenGroupOption(id: string, name: string, directoryName: string): GroupSelectOption {
    return {
        value: id,
        label: name,
        group: {
            id,
            identifier: name,
            provider: "",
            directoryId: "",
            directoryName,
        },
        source: "hidden",
    };
}

// A groupId that resolves to nothing at all - not hidden, just gone (deleted
// outright, or its directory was removed) - is preserved the same way rather
// than silently dropped the moment the editor loads. The admin sees it,
// still deconflicted, and has to explicitly pick a replacement to clear it.
function unknownGroupOption(id: string, name: string, directoryName: string): GroupSelectOption {
    return {
        value: id,
        label: name,
        group: {
            id,
            identifier: name,
            provider: "",
            directoryId: "",
            directoryName,
        },
        source: "unknown",
    };
}

// Exact conditions match a group by name; picking one from the list also
// attaches its id, which only serves to deconflict two groups that share a
// name across different directories. Pattern conditions match by name across
// every group and never carry an id.

export function GroupFilterOption(props: GroupFilterOptionsProps) {
    const login = useLogin();
    const [isOpen, setIsOpen] = useState<boolean>(true);


    const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);
    // Tracked separately from groupOptions.length: a successful fetch that
    // returns zero groups must still be able to clear a stale groupId, which
    // an empty-array check alone can't distinguish from "hasn't loaded yet."
    const [groupOptionsLoaded, setGroupOptionsLoaded] = useState(false);


    const init = async () => {
        const req = login.GetAxios();

        if (!req) {
            return;
        }

        try {
            const groups = await req.get("/api/user/groups", {
                params: {
                    "skiphidden": "true"
                }
            });

            const options = groups.data.groups
                .map((x: any) => ({
                    id: x.id as string,
                    identifier: x.identifier as string,
                    provider: x.provider as string,
                    directoryId: x.directoryId || "",
                    directoryName: x.directoryName || "",
                }))
                .sort((a: GroupOption, b: GroupOption) =>
                    a.directoryName.localeCompare(b.directoryName)
                    || a.directoryId.localeCompare(b.directoryId)
                    || a.identifier.localeCompare(b.identifier));
            setGroupOptions(options);
            // Only on success - a failed fetch must not be read as "confirmed
            // empty," or it would wrongly clear a groupId the request simply
            // couldn't check.
            setGroupOptionsLoaded(true);
        } catch {
            console.error("could not fetch group filters");
        }
    };

    useEffect(() => {
        init();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onChangeValue = (newValue: string) => {
        props.setState({
            ...props.state!,
            filter: newValue,
        });
    };

    const onChangeGroup = (group: GroupOption) => {
        props.setState({
            ...props.state!,
            filter: group.identifier,
            groupId: group.id || undefined,
            groupName: group.identifier || undefined,
            directoryName: group.directoryName || undefined,
            // A newly picked group is never hidden by definition (hidden
            // groups aren't offered by the picker) - clear a stale true left
            // over from whatever was previously selected.
            groupHidden: undefined,
        });
    };

    return (
        <>
            {
                <FormControl sx={{ marginTop: "0px" }} component="fieldset">
                    <FormLabel id={props.id} component="legend" sx={{ display: "flex", alignItems: "center", cursor: "pointer", ":hover": { color: "primary.main" } }}
                        onClick={() => setIsOpen(!isOpen)} >
                        {isOpen ? <ArrowDropDown /> : <ArrowRight />}<Typography component={"span"} sx={{ fontWeight: "bold" }}>Groups</Typography>
                    </FormLabel>
                    <Divider variant="fullWidth" sx={{ marginTop: "5px" }} />
                    {isOpen ? <RadioGroup
                        aria-label="obfuscated"
                        name="radio-obfuscated"
                        sx={{ marginLeft: "25px" }}
                        value={props.state?.conditionType || HeaderConditionType.Empty}
                        onChange={(e, value) => {
                            if (value === "") {
                                props.setState(undefined);
                            } else {
                                const exact = value === HeaderConditionType.Equal || value === HeaderConditionType.NotEqual;
                                props.setState({
                                    filterType: "group",
                                    conditionType: value as HeaderConditionType,
                                    // Switching between equal/not_equal keeps the picked group (name
                                    // included) - only crossing into/out of the pattern conditions
                                    // clears it, since a name pattern is a different kind of value.
                                    filter: exact ? props.state?.filter || "" : "",
                                    groupId: exact ? props.state?.groupId : undefined,
                                    groupName: exact ? props.state?.groupName : undefined,
                                    directoryName: exact ? props.state?.directoryName : undefined,
                                    groupHidden: exact ? props.state?.groupHidden : undefined,
                                });
                            }
                        }}
                    >
                        <FormControlLabel value="equal" control={<Radio />} label="is equal" />
                        {props.state?.conditionType === HeaderConditionType.Equal && <GroupSelect id="select_group" value={props.state.filter} groupId={props.state.groupId} groupName={props.state.groupName} directoryName={props.state.directoryName} groupHidden={props.state.groupHidden} options={groupOptions} optionsLoaded={groupOptionsLoaded} onChange={onChangeGroup} />}
                        <FormControlLabel value="not_equal" control={<Radio />} label="is not equal" />
                        {props.state?.conditionType === HeaderConditionType.NotEqual && <GroupSelect id="select_group" value={props.state.filter} groupId={props.state.groupId} groupName={props.state.groupName} directoryName={props.state.directoryName} groupHidden={props.state.groupHidden} options={groupOptions} optionsLoaded={groupOptionsLoaded} onChange={onChangeGroup} />}
                        <FormControlLabel value="contains" control={<Radio />} label="contains" />
                        {props.state?.conditionType === HeaderConditionType.Contains ? <TextField size="small" variant="outlined" value={props.state.filter} onChange={(e) => onChangeValue(e.target.value)} /> : null}
                        <FormControlLabel value="not_contains" control={<Radio />} label="does not contain" />
                        {props.state?.conditionType === HeaderConditionType.DoesNotContain ? <TextField size="small" variant="outlined" value={props.state.filter} onChange={(e) => onChangeValue(e.target.value)} /> : null}
                    </RadioGroup> : null}
                </FormControl>
            }
        </>);
}

export interface GroupSelectProps {
    id: string
    value: string
    groupId?: string
    groupName?: string
    directoryName?: string
    groupHidden?: boolean
    options: GroupOption[]
    // Whether options reflects a completed, successful fetch - options.length
    // alone can't tell "not loaded yet" apart from "loaded, genuinely empty."
    optionsLoaded?: boolean
    onChange: (v: GroupOption) => void;
}

export function GroupSelect(props: GroupSelectProps) {
    const collapsibleGroups = useCollapsibleAutocompleteGroups();

    const options: GroupSelectOption[] = props.options.map(x => ({
        value: x.id,
        label: x.identifier,
        group: x,
        source: "directory",
    }));
    const foundAmongVisibleOptions = props.groupId ? options.some(x => x.value === props.groupId) : false;
    // The current value must resolve to an option in the base list, not just
    // the search-filtered one, or the controlled Autocomplete can't display it.
    if (props.value && !props.groupId) {
        options.push(customGroupOption(props.value));
    } else if (props.groupId && props.groupHidden && !foundAmongVisibleOptions) {
        options.push(hiddenGroupOption(props.groupId, props.groupName || props.value, props.directoryName || ""));
    } else if (props.groupId && !props.groupHidden && !foundAmongVisibleOptions && props.optionsLoaded) {
        // Only render as unknown once the fetch has actually completed - before
        // that, "not among the options yet" just means "hasn't loaded yet," not
        // "gone," and treating it as unknown prematurely would flash incorrectly
        // for a group that's perfectly fine.
        options.push(unknownGroupOption(props.groupId, props.groupName || props.value, props.directoryName || ""));
    }

    const selectedOption = props.groupId
        ? options.find(x => x.value === props.groupId) || null
        : (props.value ? options.find(x => x.source === "custom") || null : null);

    return <Autocomplete
        size="small"
        disableClearable={true as boolean}
        sx={{ minWidth: 250 }}
        id={props.id}
        options={options}
        filterOptions={(availableOptions, state) => {
            const filtered = filterGroupOptions(availableOptions, state);
            const identifier = state.inputValue.trim();
            const custom = customGroupOption(identifier);
            if (identifier && !filtered.some(option => option.source === "custom" && option.value === custom.value)) {
                filtered.push(custom);
            }
            return filtered;
        }}
        open={collapsibleGroups.open}
        onOpen={collapsibleGroups.onOpen}
        onClose={collapsibleGroups.onClose}
        groupBy={(option) => option.source === "custom"
            ? customGroupKey
            : option.source === "hidden"
                ? hiddenGroupKey
                : option.source === "unknown"
                    ? unknownGroupKey
                    : collapsibleAutocompleteGroupKey(option.group.directoryId, option.group.directoryName)}
        renderGroup={collapsibleGroups.renderGroup}
        onInputChange={collapsibleGroups.onInputChange}
        slotProps={groupedSectionSlotProps}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={optionValuesEqual}
        value={selectedOption}
        onChange={(_, option) => props.onChange(option?.group || { id: "", identifier: "", provider: "", directoryId: "", directoryName: "" })}
        renderOption={(liProps, option, state) => option.source === "custom"
            ? <li {...liProps} key={option.value}>{`Use group name "${option.label}"`}</li>
            : option.source === "hidden"
                ? <li {...liProps} key={option.value}>{`${option.label} (hidden)`}</li>
                : option.source === "unknown"
                    ? <li {...liProps} key={option.value}>{`${option.label} (unknown)`}</li>
                    : renderOptionWithHighlight(liProps, option, state, false, selectedOption?.label)}
        renderInput={(params) => searchTextField(params, {
            groupToggleInputBlur: collapsibleGroups.onInputBlurToGroupToggle,
        })}
    />;
}
